import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { FileText, LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { apiGet, apiRequest, errorMessage } from '@/lib/api'
import { ROTAS_DE_CONTRATOS } from '@/lib/endpoints'
import { usePermissao } from '@/lib/permissoes'

/**
 * O template de contrato do tenant — o HTML com variáveis de onde todo PDF sai.
 *
 * ---------------------------------------------------------------------------
 * SALVAR MUDA O FUTURO, NUNCA O PASSADO
 * ---------------------------------------------------------------------------
 * O template vale para os contratos GERADOS daqui em diante; os já gerados
 * guardam o PDF e o hash SHA-256 que tinham — é o hash que prova que o
 * assinado é o gerado, e um template retroativo quebraria essa prova. Por
 * isso o salvar pede confirmação com essa frase, e não por cerimônia.
 *
 * ---------------------------------------------------------------------------
 * A PERMISSÃO É `tenant.update`, NÃO `contract.*`
 * ---------------------------------------------------------------------------
 * O mesmo racional do controller da API: o template é CONFIGURAÇÃO da
 * locadora, não operação de reserva — quem gera contrato no balcão não
 * reescreve a cláusula de caução. Leitura exige `tenant.read`.
 *
 * ---------------------------------------------------------------------------
 * EDITOR CRU DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * Um `<textarea>` monoespaçado, sem biblioteca de editor: o renderizador da
 * API só entende h1/h2/p/strong e variáveis `{{assim}}` — um editor rico
 * produziria markup que o PDF ignora em silêncio. A ajuda lateral insere a
 * variável NO CURSOR, porque ninguém decora `{{conta.documento}}`.
 */

// ---------------------------------------------------------------------------
// Contrato com a API — espelho de `contratos.dto.ts`
// ---------------------------------------------------------------------------

const templateDaApiSchema = z.object({
  corpo: z.string(),
  /** `true` = o tenant nunca salvou; vale o padrão do seed/código. */
  padrao: z.boolean(),
})

type TemplateDaApi = z.infer<typeof templateDaApiSchema>

/** Os limites do `salvarTemplateSchema` da API, para o erro nascer na tela. */
const TAMANHO_MINIMO = 20
const TAMANHO_MAXIMO = 100_000

const CHAVE_DO_TEMPLATE = 'template-de-contrato'

function useTemplate(): UseQueryResult<TemplateDaApi> {
  return useQuery({
    queryKey: [CHAVE_DO_TEMPLATE],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_CONTRATOS.template, templateDaApiSchema, { signal }),
  })
}

function useSalvarTemplate(): UseMutationResult<TemplateDaApi, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (corpo: string) =>
      apiRequest(ROTAS_DE_CONTRATOS.template, {
        schema: templateDaApiSchema,
        method: 'PUT',
        body: { corpo },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DO_TEMPLATE] })
    },
  })
}

// ---------------------------------------------------------------------------
// As variáveis que o renderizador conhece
// ---------------------------------------------------------------------------

/**
 * Espelho do mapa `variaveis` de `contratos.service.ts` — a lista fechada que
 * o `contrato-pdf.ts` substitui. Variável fora desta lista vira "—" VISÍVEL no
 * PDF (nunca string vazia), e é por isso que a ajuda lateral existe: o custo de
 * digitar errado é um traço no meio do contrato.
 */
const VARIAVEIS_DO_TEMPLATE: readonly { readonly nome: string; readonly descricao: string }[] = [
  { nome: 'tenant.nome', descricao: 'Nome da locadora' },
  { nome: 'conta.nome', descricao: 'Nome (ou razão social) do cliente' },
  { nome: 'conta.documento', descricao: 'CPF ou CNPJ do cliente' },
  { nome: 'reserva.numero', descricao: 'Número da reserva' },
  { nome: 'categoria.nome', descricao: 'Categoria contratada' },
  { nome: 'reserva.retirada', descricao: 'Data e hora da retirada (fuso da retirada)' },
  { nome: 'reserva.devolucao', descricao: 'Data e hora da devolução' },
  { nome: 'reserva.diarias', descricao: 'Quantidade de diárias' },
  { nome: 'veiculo.descricao', descricao: 'Marca, modelo, cor e placa do veículo alocado' },
  { nome: 'condutor.nome', descricao: 'Nome do condutor principal' },
  { nome: 'condutor.cnh', descricao: 'CNH do condutor principal' },
  { nome: 'reserva.total', descricao: 'Valor total da reserva, em reais' },
  { nome: 'reserva.caucao', descricao: 'Caução, em reais' },
]

const FORMATADOR_DE_CONTAGEM = new Intl.NumberFormat('pt-BR')

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function TemplateDeContratoPage(): ReactElement {
  const podeEditar = usePermissao('tenant.update')

  const consulta = useTemplate()
  const salvar = useSalvarTemplate()

  const [corpo, setCorpo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  const areaRef = useRef<HTMLTextAreaElement | null>(null)

  // Preenche o editor quando a leitura chega — uma vez. Depois disso o texto é
  // da pessoa: um refetch em segundo plano não pode apagar o que ela digitou.
  useEffect(() => {
    if (corpo === null && consulta.data !== undefined) {
      setCorpo(consulta.data.corpo)
    }
  }, [corpo, consulta.data])

  const ocupado = salvar.isPending

  /** Insere `{{variavel}}` onde o cursor está (ou no fim, sem foco prévio). */
  function inserirVariavel(nome: string): void {
    if (corpo === null || !podeEditar || ocupado) {
      return
    }

    const trecho = `{{${nome}}}`
    const elemento = areaRef.current

    const inicio = elemento?.selectionStart ?? corpo.length
    const fim = elemento?.selectionEnd ?? corpo.length

    setCorpo(`${corpo.slice(0, inicio)}${trecho}${corpo.slice(fim)}`)

    // O foco e o cursor voltam DEPOIS do re-render, senão o setSelectionRange
    // roda sobre o valor antigo e o cursor cai no lugar errado.
    requestAnimationFrame(() => {
      if (elemento !== null) {
        elemento.focus()
        const posicao = inicio + trecho.length
        elemento.setSelectionRange(posicao, posicao)
      }
    })
  }

  /** Valida na tela com os MESMOS limites da API e abre a confirmação. */
  function aoPedirParaSalvar(): void {
    if (corpo === null) {
      return
    }

    setErro(null)

    const tamanho = corpo.trim().length

    if (tamanho < TAMANHO_MINIMO) {
      setErro('Template curto demais para ser um contrato — escreva ao menos 20 caracteres.')
      return
    }

    if (corpo.length > TAMANHO_MAXIMO) {
      setErro(
        `Template grande demais — o limite é ${FORMATADOR_DE_CONTAGEM.format(TAMANHO_MAXIMO)} caracteres.`,
      )
      return
    }

    setConfirmando(true)
  }

  function aoConfirmar(): void {
    if (corpo === null) {
      return
    }

    salvar.mutate(corpo, {
      onSuccess: (resposta) => {
        setConfirmando(false)
        setCorpo(resposta.corpo)
        toast.success('Template salvo', {
          description: 'Vale para os contratos gerados a partir de agora.',
        })
      },
      onError: (causa: unknown) => {
        setConfirmando(false)
        const mensagem = errorMessage(causa)
        setErro(mensagem)
        toast.error('Não foi possível salvar o template', { description: mensagem })
      },
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Template de contrato</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          O HTML de onde todo contrato em PDF sai. Aceita h1, h2, p e strong; as variáveis{' '}
          <code className="tabular-money">{'{{assim}}'}</code> são preenchidas na geração.
        </p>
      </div>

      {podeEditar ? null : (
        <Alert variant="neutral">
          <AlertDescription>
            Somente leitura: alterar o template exige a permissão de configuração da locadora
            (tenant.update).
          </AlertDescription>
        </Alert>
      )}

      {consulta.isPending ? (
        <LoadingState rotulo="Carregando o template…" linhas={4} />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível carregar o template"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : corpo === null ? (
        <LoadingState rotulo="Carregando o template…" linhas={4} />
      ) : (
        <>
          {consulta.data.padrao ? (
            <Alert variant="warning">
              <AlertTitle>Está valendo o template padrão de fábrica</AlertTitle>
              <AlertDescription>
                Ninguém salvou um template ainda — este texto é o ponto de partida do código, não a
                minuta do jurídico da casa. Salve para fixar a versão da locadora.
              </AlertDescription>
            </Alert>
          ) : null}

          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <div className="grid items-start gap-4 lg:grid-cols-[2fr_1fr]">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Corpo do template</CardTitle>
                <p className="text-sm text-fg-secondary">
                  Tags fora de h1/h2/p/strong caem fora do PDF; variável desconhecida vira um traço
                  visível — confira o nome na lista ao lado.
                </p>
              </CardHeader>

              <CardContent className="space-y-3">
                <Textarea
                  ref={areaRef}
                  value={corpo}
                  onChange={(evento) => {
                    setCorpo(evento.target.value)
                  }}
                  rows={24}
                  maxLength={TAMANHO_MAXIMO}
                  spellCheck={false}
                  disabled={!podeEditar || ocupado}
                  aria-label="Corpo do template de contrato"
                  className="min-h-96 font-mono text-xs leading-relaxed"
                />

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs tabular-money text-fg-muted">
                    {FORMATADOR_DE_CONTAGEM.format(corpo.length)} de{' '}
                    {FORMATADOR_DE_CONTAGEM.format(TAMANHO_MAXIMO)} caracteres
                  </p>

                  {podeEditar ? (
                    <Button type="button" onClick={aoPedirParaSalvar} disabled={ocupado}>
                      {ocupado ? (
                        <LoaderCircle className="animate-spin" aria-hidden="true" />
                      ) : null}
                      Salvar template
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Variáveis disponíveis</CardTitle>
                <p className="text-sm text-fg-secondary">
                  Clique para inserir no ponto onde o cursor está.
                </p>
              </CardHeader>

              <CardContent>
                <ul className="divide-y divide-border">
                  {VARIAVEIS_DO_TEMPLATE.map((variavel) => (
                    <li key={variavel.nome}>
                      <button
                        type="button"
                        onClick={() => {
                          inserirVariavel(variavel.nome)
                        }}
                        disabled={!podeEditar || ocupado}
                        className="w-full py-2 text-left transition-colors hover:bg-elevated
                          disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="block font-mono text-xs text-foreground">
                          {`{{${variavel.nome}}}`}
                        </span>
                        <span className="block text-xs text-fg-muted">{variavel.descricao}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                <p className="mt-3 text-xs text-fg-muted">
                  Variável fora desta lista sai como “—” no PDF — de propósito, para o campo
                  faltando saltar aos olhos de quem confere.
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* A confirmação carrega a única frase que importa: muda o FUTURO. */}
      <Dialog
        open={confirmando}
        onOpenChange={(proximo) => {
          if (!proximo) {
            setConfirmando(false)
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Salvar o template?</DialogTitle>
            <DialogDescription>
              A nova versão vale para os contratos GERADOS a partir de agora. Os já gerados guardam
              o PDF e o hash que tinham — nada retroage.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setConfirmando(false)
              }}
              disabled={ocupado}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={aoConfirmar} disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              <FileText aria-hidden="true" />
              Salvar template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
